/**
 * Side Panel Component
 *
 * Unified side panel for the workspace that displays different content
 * based on the current mode.
 *
 * Modes:
 * - previewTable: Display table details (schema, versions, API links)
 * - previewLinkedTable: Display linked table details (config, API links)
 * - createTable: Display the create table wizard
 *
 * @module features/workspace/components/SidePanel
 */

import { useState, useEffect, useRef } from 'react';

import { useSidePanelStore } from '../../stores';
import { PreviewSidePanel } from '../PreviewPanel';
import { CreateTableSidePanel } from '../CreateTableSheet';
import type { PreviewItem } from '../PreviewPanel/types';

// ============================================================================
// TYPES
// ============================================================================

export interface SidePanelProps {
  /** Callback when the panel is closed */
  onClose: () => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Debounce delay for rapid item switching (ms) - matches panel animation duration */
const DEBOUNCE_DELAY = 150;

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * SidePanel component
 *
 * Renders the appropriate content based on the current panel mode.
 * Uses debouncing to handle rapid switching between items.
 */
export function SidePanel({ onClose }: SidePanelProps) {
  const { mode, previewItem } = useSidePanelStore();

  // Debounced state - what we actually render
  const [debouncedMode, setDebouncedMode] = useState(mode);
  const [debouncedItem, setDebouncedItem] = useState<PreviewItem | null>(previewItem);

  // Track the debounce timer
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce updates to handle rapid switching
  useEffect(() => {
    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // If we have a valid item, debounce the update
    if (previewItem) {
      debounceTimer.current = setTimeout(() => {
        setDebouncedMode(mode);
        setDebouncedItem(previewItem);
      }, DEBOUNCE_DELAY);
    } else if (mode === 'createTable') {
      // Create table mode - update immediately
      setDebouncedMode(mode);
      setDebouncedItem(null);
    }
    // If previewItem is null and not createTable mode, keep showing previous content

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [mode, previewItem]);

  // Render content based on debounced state
  if (debouncedMode === 'createTable') {
    return <CreateTableSidePanel onClose={onClose} />;
  }

  if ((debouncedMode === 'previewTable' || debouncedMode === 'previewLinkedTable') && debouncedItem) {
    // Use key to force remount when item changes - ensures clean state
    return (
      <PreviewSidePanel
        key={debouncedItem.id}
        item={debouncedItem}
        onClose={onClose}
      />
    );
  }

  return null;
}
